/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'helpers/store'
import { OffersAction } from 'store/offers/OffersAction'
import { CompanyAddress } from 'types/entities/CompanyAddress'
import { closeSecondaryModal } from 'helpers/actions'
import UiSelect, { UiSelectOption } from 'components/Ui/UiSelect'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import CompanyAddressForm from 'components/Companies/forms/CompanyAddressForm'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import { formatAddressSummary } from 'utils/addressUtils'

interface ChangeOfferAddressModalProps {
    offerId: number
    sellerId: number
    companyId: number | null
    initialAddressId?: number | null
    onSuccess?: () => void
}

const ChangeOfferAddressModal: React.FC<ChangeOfferAddressModalProps> = ({
    offerId,
    sellerId,
    companyId,
    initialAddressId,
    onSuccess,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [addresses, setAddresses] = React.useState<CompanyAddress[]>([])
    const [selectedAddressId, setSelectedAddressId] = React.useState<
        number | null
    >(initialAddressId || null)
    const [isLoading, setIsLoading] = React.useState(false)
    const [isSaving, setIsSaving] = React.useState(false)
    const [showCreateAddressForm, setShowCreateAddressForm] = React.useState(false)

    React.useEffect(() => {
        if (companyId) {
            setIsLoading(true)
            dispatch(CompaniesAction.fetchCompanyById(companyId))
                .then((company) => {
                    setAddresses(company.addresses || [])
                    setIsLoading(false)
                })
                .catch(() => {
                    setAddresses([])
                    setIsLoading(false)
                })
        }
    }, [companyId, dispatch])

    const handleSave = async (shouldClose: boolean = false) => {
        if (selectedAddressId === null || !companyId) return

        setIsSaving(true)
        try {
            await dispatch(
                OffersAction.updateOffer({
                    id: offerId,
                    seller_id: sellerId,
                    company_id: companyId,
                    address_id: Number(selectedAddressId),
                })
            )
            if (onSuccess) onSuccess()
            if (shouldClose) {
                dispatch(closeSecondaryModal())
            }
        } finally {
            setIsSaving(false)
        }
    }

    const addressOptions: UiSelectOption[] = addresses
        .filter((address) => address.name !== 'shipping' && address.name !== 'postal')
        .map((address) => {
        const parts = [
            address.name,
            address.department_name,
            formatAddressSummary(address.street_name, address.postal_code, address.town) || null,
            address.region,
            address.country,
            address.note ? `(${address.note})` : null,
        ].filter(Boolean)

        return {
            value: address.id,
            label: parts.join(', '),
        }
    })

    const hasAddressOptions = addressOptions.length > 0
    const shouldShowCreateAddress = !isLoading && !!companyId && !hasAddressOptions && !showCreateAddressForm

    return (
        <UiFormModalShell
            className="ChangeOfferAddressModal"
            title={t('Change Address')}
            subtitle={t('Select a new address for this offer')}
            showHeader={true}
            footer={
                showCreateAddressForm ? undefined : (
                    <UiFormActionsBar
                        className="ChangeOfferAddressModalActions w-full px-6 py-4"
                        primaryText={t('Save') as string}
                        onPrimary={() => handleSave(false)}
                        saveAndCloseText={t('Save & Close') as string}
                        onSaveAndClose={() => handleSave(true)}
                        loading={isSaving}
                        disabled={
                            !companyId ||
                            selectedAddressId === null ||
                            selectedAddressId === initialAddressId ||
                            isLoading
                        }
                    />
                )
            }
        >
            <UiFormInner>
                <div className="ChangeOfferAddressModalContent">
                    {showCreateAddressForm ? (
                        <CompanyAddressForm
                            inline={true}
                            showSaveAndClose={false}
                            onBack={() => setShowCreateAddressForm(false)}
                            onChange={(address) => {
                                setAddresses((prev) => {
                                    const withoutSameAddress = prev.filter((item) => item.id !== address.id)
                                    return [...withoutSameAddress, address]
                                })
                                setSelectedAddressId(address.id)
                                setShowCreateAddressForm(false)
                            }}
                        />
                    ) : (
                        <>
                            <UiSelect
                                label={t('Address')}
                                options={addressOptions}
                                value={selectedAddressId}
                                onChange={(val) => setSelectedAddressId(val ? Number(val) : null)}
                                placeholder={
                                    isLoading
                                        ? t('Loading...')
                                        : hasAddressOptions
                                            ? t('Choose an address')
                                            : t('No addresses available')
                                }
                                disabled={isLoading || !hasAddressOptions}
                            />
                            {shouldShowCreateAddress && (
                                <div className="ChangeOfferAddressModalCreateHint mt-2 flex items-center justify-between gap-2">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {t('This company has no address yet.')}
                                    </span>
                                    <UiButton
                                        title={t('Create new address')}
                                        style={UiButtonStyle.Secondary}
                                        size={UiButtonSize.Small}
                                        onClick={() => setShowCreateAddressForm(true)}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </UiFormInner>
        </UiFormModalShell>
    )
}

export default ChangeOfferAddressModal
